// game.js - Main game controller with scene management

import { Engine, ParticleSystem } from './engine.js';
import { storage } from './storage.js';
import { sound } from './sound.js';
import { UIController } from './ui.js';
import { TaskManager, ROOMS } from './tasks.js';

// Scene base class
class Scene {
    constructor(game) {
        this.game = game;
    }

    enter(data) { }
    exit() { }
    update(deltaTime) { }
    render(ctx) { }
}

// Menu Scene
class MenuScene extends Scene {
    enter(data) {
        const hasSave = storage.hasSave();
        this.game.ui.showMainMenu(hasSave);

        // Play menu music
        sound.playMusic('menu', true);
    }

    exit() {
        this.game.ui.hideMainMenu();
    }
}

// Hub Scene - Room view with idle character
class HubScene extends Scene {
    constructor(game) {
        super(game);
        this.assistantX = 160;
        this.assistantY = 180;
        this.idleTime = 0;
        this.nextTaskCheckTime = 0;
        this.wellnessDecayTimer = 0;
    }

    enter(data) {
        // Update UI
        this.game.ui.updateRoom(
            this.game.state.house.currentRoom,
            this.game.state.house.currentFloor
        );
        this.game.ui.updateWellness(this.game.state.player.wellness);
        this.game.ui.updateScore(this.game.state.player.score);
        this.game.ui.hideTimer();

        // Check for pending tasks
        this.checkForNewTask();

        // Play room-specific music
        const roomMusic = this.getRoomMusic(this.game.state.house.currentRoom);
        sound.playMusic(roomMusic, true);
    }

    getRoomMusic(room) {
        const musicMap = {
            // House
            [ROOMS.KITCHEN]: 'kitchen',
            [ROOMS.HALLWAY]: 'hallway',
            [ROOMS.STUDY]: 'study',
            [ROOMS.LAB]: 'lab',
            // Garden
            [ROOMS.CARAVAN]: 'caravan',
            [ROOMS.DOME]: 'dome',
            [ROOMS.SHED]: 'shed',
            [ROOMS.LAWN]: 'lawn'
        };
        return musicMap[room] || 'hallway';
    }

    checkForNewTask() {
        // Should we issue a new task?
        if (this.game.taskManager.shouldIssueTask(this.game.state)) {
            setTimeout(() => {
                // Get task for current room (makes rooms meaningful!)
                const currentRoom = this.game.state.house.currentRoom;
                let task = this.game.taskManager.getTaskForRoom(currentRoom);

                // Fallback to random task if no room-specific tasks available
                if (!task) {
                    task = this.game.taskManager.getRandomTask();
                }

                if (task) {
                    this.game.state.tasks.current = task;
                    this.game.state.tasks.lastTaskTime = Date.now();
                    this.game.ui.showTaskCard(task);
                }
            }, 2000); // Show task after 2 seconds
        }
    }

    update(deltaTime) {
        this.idleTime += deltaTime;

        // Wellness decay over time (every 30 seconds in game)
        this.wellnessDecayTimer += deltaTime;
        if (this.wellnessDecayTimer >= 30) {
            this.wellnessDecayTimer = 0;
            this.game.state.player.wellness = Math.max(0, this.game.state.player.wellness - 0.5);
            this.game.ui.updateWellness(this.game.state.player.wellness);

            // Check for game over
            if (this.game.state.player.wellness <= 0) {
                this.game.changeScene('end', {
                    title: 'CONTESTANT EXPIRED',
                    message: 'Your contestant ran out of wellness. Better luck next time!',
                    finalScore: this.game.state.player.score
                });
            }
        }

        // Simple idle animation for assistant
        this.assistantY = 180 + Math.sin(this.idleTime * 2) * 2;

        // Check for new task periodically
        this.nextTaskCheckTime += deltaTime;
        if (this.nextTaskCheckTime >= 60) { // Check every minute
            this.nextTaskCheckTime = 0;
            this.checkForNewTask();
        }
    }

    render(ctx) {
        const engine = this.game.engine;

        // Draw room background
        const roomColor = this.getRoomColor(this.game.state.house.currentRoom);
        engine.drawRect(0, 0, 320, 240, roomColor);

        // Draw floor tiles
        this.drawFloor(engine);

        // Draw assistant character (simple rectangle for now)
        this.drawAssistant(engine);

        // Draw room label with contrasting color
        const textColor = this.getRoomTextColor(this.game.state.house.currentRoom);
        engine.drawText(
            this.game.state.house.currentRoom.toUpperCase(),
            160, 20, 12, textColor, 'center'
        );

        // Show helper text
        engine.drawText(
            'Press MAP to navigate',
            160, 220, 8, '#A0A0A0', 'center'
        );
    }

    getRoomColor(room) {
        const colors = {
            // Ground Floor (House)
            [ROOMS.KITCHEN]: '#F4E8C1',
            [ROOMS.HALLWAY]: '#A0A0A0',
            [ROOMS.STUDY]: '#8B7355',
            [ROOMS.LAB]: '#6B6B6B',
            // Garden Locations
            [ROOMS.CARAVAN]: '#C8B299',
            [ROOMS.DOME]: '#E0E0E0',
            [ROOMS.SHED]: '#6B4423',
            [ROOMS.LAWN]: '#4CAF50'
        };
        return colors[room] || '#A0A0A0';
    }

    getRoomTextColor(room) {
        // Return contrasting text color for each room background
        const textColors = {
            // Ground Floor (House)
            [ROOMS.KITCHEN]: '#8B1538',      // Dark red on light beige
            [ROOMS.HALLWAY]: '#F4E8C1',      // Light beige on gray
            [ROOMS.STUDY]: '#F4E8C1',        // Light beige on brown
            [ROOMS.LAB]: '#F4E8C1',          // Light beige on dark gray
            // Garden Locations
            [ROOMS.CARAVAN]: '#8B1538',      // Dark red on tan
            [ROOMS.DOME]: '#8B1538',         // Dark red on light gray
            [ROOMS.SHED]: '#F4E8C1',         // Light beige on dark brown
            [ROOMS.LAWN]: '#F4E8C1'          // Light beige on green
        };
        return textColors[room] || '#F4E8C1';
    }

    drawFloor(engine) {
        // Simple tiled floor pattern
        for (let y = 180; y < 240; y += 16) {
            for (let x = 0; x < 320; x += 16) {
                const offset = ((x + y) / 16) % 2;
                const color = offset ? '#8B8B8B' : '#7A7A7A';
                engine.drawRect(x, y, 16, 16, color);
            }
        }
    }

    drawAssistant(engine) {
        // Simple 8-bit assistant character
        // Body (black suit)
        engine.drawRect(this.assistantX - 8, this.assistantY - 12, 16, 20, '#1a1a1a');

        // Shirt (white)
        engine.drawRect(this.assistantX - 4, this.assistantY - 8, 8, 12, '#FFFFFF');

        // Head (skin tone)
        engine.drawRect(this.assistantX - 6, this.assistantY - 20, 12, 10, '#F4C2A0');

        // Hair
        engine.drawRect(this.assistantX - 6, this.assistantY - 22, 12, 4, '#4A3728');

        // Eyes
        engine.drawRect(this.assistantX - 4, this.assistantY - 18, 2, 2, '#1a1a1a');
        engine.drawRect(this.assistantX + 2, this.assistantY - 18, 2, 2, '#1a1a1a');

        // LEFT SIDE: Task envelope (manila color)
        engine.drawRect(this.assistantX - 12, this.assistantY - 6, 6, 8, '#D9C89E');

        // Red wax seal on task envelope
        engine.drawCircle(this.assistantX - 9, this.assistantY - 2, 2, '#8B1538');

        // Gold "T" mark on seal
        engine.drawRect(this.assistantX - 9, this.assistantY - 3, 1, 3, '#D4AF37');

        // RIGHT SIDE: Arm holding clipboard
        // Right arm (skin tone) - extends from body to clipboard
        engine.drawRect(this.assistantX + 8, this.assistantY - 8, 3, 10, '#F4C2A0');

        // Hand (skin tone)
        engine.drawRect(this.assistantX + 11, this.assistantY - 4, 2, 4, '#F4C2A0');

        // Clipboard paper (white)
        engine.drawRect(this.assistantX + 10, this.assistantY - 6, 6, 8, '#FFFFFF');

        // Clipboard clip (silver/gray)
        engine.drawRect(this.assistantX + 11, this.assistantY - 7, 4, 2, '#A8A8A8');

        // Clipboard backing/shadow for depth
        engine.drawRect(this.assistantX + 16, this.assistantY - 6, 1, 8, '#666666');
        engine.drawRect(this.assistantX + 10, this.assistantY + 2, 6, 1, '#666666');
    }
}

// Task Scene - Execute mini-game
class TaskScene extends Scene {
    constructor(game) {
        super(game);
        this.task = null;
        this.timer = null;
        this.score = 0;
        this.accuracy = 0;
        this.mechanic = null;
    }

    enter(data) {
        this.task = data.task;
        this.game.ui.showTimer();

        // Create timer
        this.timer = this.game.engine.createTimer(this.task.duration, () => {
            this.endTask(false); // Time's up
        });

        // Initialize mechanic-specific state
        this.initMechanic();

        sound.playTaskStartSound();
    }

    initMechanic() {
        // Initialize based on task mechanic type
        switch (this.task.mechanic) {
            case 'quick_tap':
                this.initQuickTap();
                break;
            case 'rhythm':
                this.initRhythm();
                break;
            case 'drag_stack':
                this.initDragStack();
                break;
            case 'tap_juggle':
                this.initTapJuggle();
                break;
            case 'memory':
                this.initMemory();
                break;
            case 'stealth':
                this.initStealth();
                break;
            default:
                this.initQuickTap(); // Fallback
        }
    }

    // Quick Tap mechanic (e.g., Cushion Chaos)
    initQuickTap() {
        this.mechanic = {
            type: 'quick_tap',
            targets: [],
            hits: 0,
            misses: 0,
            spawnTimer: 0,
            spawnRate: 1.5
        };
    }

    updateQuickTap(deltaTime) {
        const m = this.mechanic;

        // Spawn targets
        m.spawnTimer += deltaTime;
        if (m.spawnTimer >= m.spawnRate) {
            m.spawnTimer = 0;
            m.targets.push({
                x: this.game.engine.randomInt(20, 300),
                y: 40,
                vy: 30 + this.game.engine.randomInt(0, 20),
                size: 16,
                color: '#D4AF37'
            });
        }

        // Update targets
        for (let i = m.targets.length - 1; i >= 0; i--) {
            const target = m.targets[i];
            target.y += target.vy * deltaTime;

            // Remove if off screen
            if (target.y > 200) {
                m.targets.splice(i, 1);
                m.misses++;
                sound.playFailSound();

                if (m.misses >= 3) {
                    this.endTask(false);
                }
            }
        }

        // Check for taps
        if (this.game.engine.wasPointerClicked()) {
            const pos = this.game.engine.getPointerPosition();

            for (let i = m.targets.length - 1; i >= 0; i--) {
                const target = m.targets[i];
                const dist = this.game.engine.distance(pos.x, pos.y, target.x, target.y);

                if (dist < target.size) {
                    m.targets.splice(i, 1);
                    m.hits++;
                    sound.playTapSound();
                    this.game.particles.emit(target.x, target.y, 5, { color: '#D4AF37' });
                    break;
                }
            }
        }

        // Calculate accuracy
        const total = m.hits + m.misses;
        this.accuracy = total > 0 ? m.hits / total : 0;
    }

    renderQuickTap(ctx) {
        const m = this.mechanic;

        // Draw targets
        for (const target of m.targets) {
            this.game.engine.drawCircle(target.x, target.y, target.size, target.color);
        }

        // Draw score
        this.game.engine.drawText(`Hits: ${m.hits}`, 10, 10, 10, '#F4E8C1');
        this.game.engine.drawText(`Misses: ${m.misses}/3`, 10, 25, 10, '#F4E8C1');
    }

    // Rhythm mechanic (placeholder)
    initRhythm() {
        this.mechanic = {
            type: 'rhythm',
            beats: [],
            score: 0,
            nextBeat: 1.0
        };
    }

    updateRhythm(deltaTime) {
        // Simplified rhythm game
        const m = this.mechanic;
        m.nextBeat -= deltaTime;

        if (m.nextBeat <= 0) {
            m.nextBeat = 0.8;
            m.beats.push({ x: 320, y: 120, targetX: 40, hit: false });
        }

        // Move beats
        for (let i = m.beats.length - 1; i >= 0; i--) {
            const beat = m.beats[i];
            beat.x -= 100 * deltaTime;

            if (beat.x < 20 && !beat.hit) {
                m.beats.splice(i, 1);
            }
        }

        // Check for taps
        if (this.game.engine.wasPointerClicked()) {
            for (let i = m.beats.length - 1; i >= 0; i--) {
                const beat = m.beats[i];
                const dist = Math.abs(beat.x - beat.targetX);

                if (dist < 20 && !beat.hit) {
                    beat.hit = true;
                    m.score += 10;
                    sound.playTapSound();
                    m.beats.splice(i, 1);
                    break;
                }
            }
        }

        this.accuracy = Math.min(m.score / 100, 1);
    }

    renderRhythm(ctx) {
        const m = this.mechanic;

        // Draw target zone
        this.game.engine.drawRectOutline(30, 110, 20, 20, '#D4AF37', 3);

        // Draw beats
        for (const beat of m.beats) {
            this.game.engine.drawRect(beat.x - 10, beat.y - 10, 20, 20, '#8B1538');
        }

        this.game.engine.drawText(`Score: ${m.score}`, 10, 10, 10, '#F4E8C1');
    }

    // Other mechanics (stubs for now)
    initDragStack() { this.initQuickTap(); } // Fallback
    initTapJuggle() { this.initQuickTap(); }
    initMemory() { this.initQuickTap(); }
    initStealth() { this.initRhythm(); }

    update(deltaTime) {
        // Update timer
        this.game.engine.updateTimer(this.timer, deltaTime);
        const remaining = this.game.engine.getTimerRemaining(this.timer);
        this.game.ui.updateTimer(remaining);

        // Update mechanic
        switch (this.mechanic.type) {
            case 'quick_tap':
                this.updateQuickTap(deltaTime);
                break;
            case 'rhythm':
                this.updateRhythm(deltaTime);
                break;
        }

        // Update particles
        this.game.particles.update(deltaTime);
    }

    render(ctx) {
        const engine = this.game.engine;

        // Draw background
        engine.drawRect(0, 0, 320, 240, '#2A2A2A');

        // Draw task title
        engine.drawText(
            this.task.title.toUpperCase(),
            160, 10, 12, '#D4AF37', 'center'
        );

        // Draw instructions with text wrapping (max width 300px with 10px padding on each side)
        const instructionsHeight = engine.drawWrappedText(
            this.task.instructions,
            160, 28, 300, 10, '#F4E8C1', 'center', 1.4
        );

        // Render mechanic
        switch (this.mechanic.type) {
            case 'quick_tap':
                this.renderQuickTap(ctx);
                break;
            case 'rhythm':
                this.renderRhythm(ctx);
                break;
        }

        // Render particles
        this.game.particles.render(ctx);
    }

    endTask(completed) {
        const timeBonus = completed ? this.game.engine.getTimerRemaining(this.timer) / this.task.duration : 0;
        const taskScore = this.game.taskManager.calculateScore(this.task, this.accuracy, timeBonus);

        // Update game state
        this.game.state.player.score += taskScore;
        this.game.state.tasks.tasksAttempted++;

        if (completed) {
            this.game.state.player.wellness += this.task.wellnessBonus;
            this.game.state.tasks.completed.push(this.task.id);
            this.game.state.tasks.tasksCompleted++;
            sound.playSuccessSound();
            this.game.ui.showToast(`Task Complete! +${taskScore} points`);
        } else {
            this.game.state.player.wellness += this.task.wellnessPenalty;
            this.game.state.tasks.failed.push(this.task.id);
            sound.playFailSound();
            this.game.ui.showToast(`Task Failed! ${this.task.wellnessPenalty} wellness`);
        }

        // Clamp wellness
        this.game.state.player.wellness = Math.max(0, Math.min(100, this.game.state.player.wellness));

        // Check for room unlocks (based on score milestones)
        if (completed) {
            this.checkRoomUnlocks();
        }

        // Save game
        storage.save(this.game.state);

        // Return to hub
        setTimeout(() => {
            this.game.changeScene('hub');
        }, 2000);
    }

    checkRoomUnlocks() {
        const score = this.game.state.player.score;
        const unlocked = this.game.state.house.unlockedRooms;

        const unlockThresholds = [
            { score: 50, room: 'caravan', name: 'Caravan (Garden)' },
            { score: 100, room: 'dome', name: 'Dome (Garden)' },
            { score: 150, room: 'shed', name: 'Shed (Garden)' },
            { score: 200, room: 'lawn', name: 'Lawn (Garden)' }
        ];

        for (const threshold of unlockThresholds) {
            if (score >= threshold.score && !unlocked.includes(threshold.room)) {
                unlocked.push(threshold.room);
                this.game.ui.showToast(`🎉 NEW LOCATION UNLOCKED: ${threshold.name}!`, 4000);
                sound.playSuccessSound();
            }
        }
    }

    exit() {
        this.game.ui.hideTimer();
    }
}

// End Scene
class EndScene extends Scene {
    enter(data) {
        this.game.ui.showEndScreen(
            data.title,
            data.message,
            data.finalScore
        );

        sound.stopMusic();
        sound.playFailSound();
    }

    exit() {
        this.game.ui.hideEndScreen();
    }
}

// Main Game Controller
class Game {
    constructor() {
        this.canvas = document.getElementById('game-canvas');
        this.engine = new Engine(this.canvas);
        this.particles = new ParticleSystem(this.engine);

        // Initialize game state
        this.state = storage.load();
        storage.applyWellnessDecay(this.state);

        // Initialize managers
        this.ui = new UIController(this.state);
        this.taskManager = new TaskManager(this.state);

        // Initialize scenes
        this.scenes = {
            menu: new MenuScene(this),
            hub: new HubScene(this),
            task: new TaskScene(this),
            end: new EndScene(this)
        };

        this.currentScene = null;

        // Setup UI callbacks
        this.setupUICallbacks();

        // Initialize audio
        sound.init();

        // Auto-save every 60 seconds
        setInterval(() => {
            if (this.state) {
                storage.save(this.state);
            }
        }, 60000);
    }

    setupUICallbacks() {
        // Task card callbacks
        this.ui.onStartTask = () => {
            this.ui.hideTaskCard();
            this.changeScene('task', { task: this.state.tasks.current });
        };

        // Map callbacks
        this.ui.onRoomSelect = (roomId, floorName) => {
            console.log(`Room selected: ${roomId} on floor: ${floorName}`);
            this.state.house.currentRoom = roomId;
            this.state.house.currentFloor = floorName;
            console.log('State updated. Current room now:', this.state.house.currentRoom);

            // Add to visited rooms
            if (!this.state.house.visitedRooms.includes(roomId)) {
                this.state.house.visitedRooms.push(roomId);
            }

            this.changeScene('hub');
        };

        // Menu callback
        this.ui.onReturnToMenu = () => {
            // Save game before returning to menu
            if (storage.save(this.state)) {
                console.log('Game saved before returning to menu');
            }
            this.changeScene('menu');
            sound.playMusic('menu', true);
        };

        // Settings callbacks
        this.ui.onSaveGame = () => {
            if (storage.save(this.state)) {
                this.ui.showToast('Game saved!');
            } else {
                this.ui.showToast('Failed to save game');
            }
        };

        this.ui.onResetGame = () => {
            if (confirm('Are you sure you want to reset? All progress will be lost.')) {
                storage.deleteSave();
                this.ui.showToast('Game reset!');
                setTimeout(() => location.reload(), 1000);
            }
        };

        // End screen callbacks
        this.ui.onTryAgain = () => {
            this.state = storage.getDefaultState();
            // Update UI and TaskManager references to new state object
            this.ui.gameState = this.state;
            this.taskManager.gameState = this.state;
            this.changeScene('hub');
        };

        this.ui.onReturnToMenu = () => {
            this.changeScene('menu');
        };

        // Main menu callbacks
        this.ui.onNewGame = () => {
            this.state = storage.getDefaultState();
            storage.save(this.state);
            // Update UI and TaskManager references to new state object
            this.ui.gameState = this.state;
            this.taskManager.gameState = this.state;
            this.changeScene('hub');
        };

        this.ui.onContinueGame = () => {
            this.state = storage.load();
            storage.applyWellnessDecay(this.state);
            // Update UI and TaskManager references to new state object
            this.ui.gameState = this.state;
            this.taskManager.gameState = this.state;
            this.changeScene('hub');
        };
    }

    changeScene(sceneName, data = {}) {
        if (this.currentScene) {
            this.currentScene.exit();
        }

        this.currentScene = this.scenes[sceneName];
        if (this.currentScene) {
            this.currentScene.enter(data);
        }
    }

    start() {
        // Start with menu scene
        this.changeScene('menu');

        // Start game loop
        this.engine.start(
            (deltaTime) => this.update(deltaTime),
            (ctx) => this.render(ctx)
        );
    }

    update(deltaTime) {
        if (this.currentScene) {
            this.currentScene.update(deltaTime);
        }
    }

    render(ctx) {
        if (this.currentScene) {
            this.currentScene.render(ctx);
        }
    }
}

// Initialize game when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
    const game = new Game();
    game.start();
});
